import React, {forwardRef, useEffect, useRef} from 'react';

import mergeRefs from 'merge-refs';

import {ElementGroup, Indent} from '@pb/kit-component-element-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import comboBoxStyles from './combo-box.component.module.styl';

import type {IconType} from '@pb/kit-component-icon';
import type {InputProps} from '@pb/kit-component-input-v2';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';

const classes = hashedClasses(comboBoxStyles);
const classNames = withCssModule(comboBoxStyles);

type ItemProps = {
    caption: string,
    iconBefore?: IconType,
    meta?: ReactNode,
};

type NativeProps = Omit<InputProps, 'value'>;

export interface ComboBoxInputProps extends NativeProps {
    /**
     * Allowed to select multiple items. If true - value should be an array
     * */
    multiselectable?: boolean,

    /**
     * Text to be shown if no item, or default item is selected
     */
    placeholder?: string,


    /**
     * Function will transform caption of selected items into ComboBox caption
     */
    captionTransform?: (captions: string) => string,

    /**
     * This icon will appear after caption
     */
    iconAfter?: IconType,

    /**
     * Show meta information of selected item
     */
    showSelectedItemMeta?: boolean,

    /**
     * Get it from useComboboxValue
     * */
    selectedItemsProps: ItemProps[],

    /**
     * Get it from useComboboxValue
     * */
    isDefaultItemSelected?: boolean,

    /**
     * On Enter or Space key down
     * */
    onKeyDownHandler?: () => void,
}

/**
 * ComboBox Input component
 *
 * Renders a ComboBox Input. For internal use only.
 */
export const ComboBoxInput = forwardRef<HTMLInputElement, ComboBoxInputProps>(function ComboBoxInput(
    props, ref,
) {
    const {
        captionTransform,
        placeholder,
        color,
        showSelectedItemMeta,
        multiselectable,
        selectedItemsProps,
        isDefaultItemSelected,
        onKeyDownHandler,
        ...nativeProps
    } = props;
    const comboboxInputRef = useRef<HTMLInputElement>(null);

    const renderIconBefore = () => {
        if (multiselectable) {
            return null;
        }
        const iconBefore = selectedItemsProps[0]?.iconBefore;

        if (iconBefore) {
            return (
                <Icon
                    aria-hidden={true}
                    type={iconBefore}
                />
            );
        }

        return null;
    };

    const isMetaDataShown = showSelectedItemMeta && !multiselectable && selectedItemsProps[0]?.meta;

    let captions = selectedItemsProps.map(({caption}) => caption).join(', ');
    if (captionTransform) {
        captions = captionTransform(captions);
    }

    const value = isDefaultItemSelected
        ? ''
        : captions;


    useEffect(() => {
        if (comboboxInputRef.current && value && value.length !== 0) {
            const input = comboboxInputRef.current;

            setTimeout(() => {
                input.scrollLeft = 0;
            }, 0);
        }
    }, [value]);

    return (
        <Input
            inputComponent={ReadonlyInput as InputProps['inputComponent']}
            placeholder={
                isDefaultItemSelected
                    ? captions
                    : placeholder
            }
            value={value}
            autoCapitalize="none"
            spellCheck="false"
            aria-autocomplete="list"
            color={color}
            startAdornment={renderIconBefore()}
            endAdornment={(
                <ElementGroup indent={Indent.S} mix="pointer-events-none">
                    {isMetaDataShown ? (
                        <span className={classNames('combo-box__meta-data pointer-events-auto')}>
                            {selectedItemsProps[0].meta}
                        </span>
                    ) : null}
                    {color === 'ERROR' ? (
                        <Icon type={Icons.WARNING} className="pointer-events-auto error-color"/>
                    ) : null}
                    <Icon
                        type={Icons.ANGLE_DOWN}
                        className={classes['combo-box__icon']}
                    />
                </ElementGroup>
            )}
            {...nativeProps}
            onKeyDown={event => {
                if (
                    event.key === 'Enter' || event.key === ' '
                ) {
                    onKeyDownHandler?.();
                    event.preventDefault();
                }
                nativeProps.onKeyDown?.(event);
            }}
            className={classNames('combo-box_input', nativeProps.className)}
            ref={mergeRefs(ref, comboboxInputRef)}
        />
    );
});

ComboBoxInput.displayName = 'ComboBoxInput';

type ReadonlyInputProps = ComponentPropsWithoutRef<'input'>;

const ReadonlyInput = forwardRef<HTMLInputElement, ReadonlyInputProps>((props, ref) => (
    <input
        ref={ref}
        readOnly={true}
        {...props}
    />
));

ReadonlyInput.displayName = 'ReadonlyInput';
